import dayjs from "dayjs";
import SEPA from "sepa";
import { renderRemittanceTemplate } from "./remittanceTokens";

// ---------- Dates / Labels ----------
export function defaultBookMonth() {
  const d = dayjs().add(1, "month");
  return `${d.format("MM")}/${d.format("YYYY")}`;
}

export function monthOptions() {
  return Array.from({ length: 12 }).map((_, idx) => {
    const d = dayjs().add(idx - 5, "month");
    const v = `${d.format("MM")}/${d.format("YYYY")}`;
    return { id: idx + 1, value: v, label: v };
  });
}

export function sanitizeTextForSEPA(s = "") {
  return String(s)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .trim();
}

// Validate BIC (8 or 11 chars, SWIFT format)
export function isValidBIC(bic) {
  if (!bic) return false;
  const cleaned = String(bic).replace(/\s+/g, "").toUpperCase();
  // 4 letters (bank) + 2 letters (country) + 2 alnum (location) + optional 3 alnum (branch)
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(cleaned);
}

// ---------- IBAN helpers ----------
export function cleanIban(iban) {
  if (!iban) return "";
  return iban.replace(/\s+/g, "").toUpperCase();
}

export function validateIBAN(ibanRaw) {
  const iban = cleanIban(ibanRaw);
  if (!/^[A-Z0-9]+$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const expanded = rearranged
    .split("")
    .map((ch) => (ch >= "A" && ch <= "Z" ? ch.charCodeAt(0) - 55 : ch))
    .join("");
  let remainder = 0;
  for (let i = 0; i < expanded.length; i += 7) {
    const block = String(remainder) + expanded.substring(i, i + 7);
    remainder = parseInt(block, 10) % 97;
  }
  return remainder === 1;
}

// ---------- MM/YYYY helpers ----------
function parseMmYyyy(str) {
  if (typeof str !== "string") return null;
  const m = str.match(/^(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const mm = +m[1];
  const yyyy = +m[2];
  if (mm < 1 || mm > 12) return null;
  return { y: yyyy, m: mm };
}

function formatMmYyyy({ y, m }) {
  const mm = String(m).padStart(2, "0");
  return `${mm}/${y}`;
}

function addMonths({ y, m }, delta) {
  const idx = y * 12 + (m - 1) + delta;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return { y: ny, m: nm };
}

function monthsDiffInclusive(payedTillStr, targetStr) {
  // # months from (payedTill + 1) .. target inclusive
  const pt = parseMmYyyy(payedTillStr);
  const tg = parseMmYyyy(targetStr);
  if (!tg) return 0;
  if (!pt) return 1; // No payedTill -> collect the target month
  const a = tg.y * 12 + tg.m;
  const b = pt.y * 12 + pt.m;
  const diff = a - b;
  return diff > 0 ? diff : 0; // <=0 means up to date
}

// Compute merged period note across members (min start .. target)
function buildPeriodNote(membersStarts, targetStr) {
  if (!membersStarts.length) return targetStr;
  // earliest start (min)
  membersStarts.sort((a, b) => {
    const A = a.y * 12 + a.m;
    const B = b.y * 12 + b.m;
    return A - B;
  });
  const minStart = membersStarts[0];
  const minStr = formatMmYyyy(minStart);
  if (minStr === targetStr) return targetStr;
  return `${minStr}–${targetStr}`;
}

/**
 * Determine whether a member should be collected for the given booking month.
 * - Active members: always eligible.
 * - Terminated members: eligible only if terminationInformation.lastMonth >= target month.
 */
function isCollectableForMonth(member, targetMmYyyy) {
  const status = (member?.status || "").toLowerCase();

  if (status === "active") return true;

  if (status === "terminated") {
    const last = member?.terminationInformation?.lastMonth;
    const lastObj = parseMmYyyy(last);
    const tgtObj = parseMmYyyy(targetMmYyyy);
    if (!tgtObj) return false; // invalid target month
    if (!lastObj) return false; // terminated but no lastMonth ⇒ be safe: do not collect
    const lastIdx = lastObj.y * 12 + lastObj.m;
    const tgtIdx = tgtObj.y * 12 + tgtObj.m;
    return lastIdx >= tgtIdx; // still within the obligation window
  }

  // For any other status, do not collect.
  return false;
}

// ---------- XML (pain.008.001.02) ----------
export function buildPain008Xml({ creditor, collectionDate, payments }) {
  // Build SEPA XML using the 'sepa' package (like createXMLSepaData)
  // creditor: { creditorName, creditorIBAN, creditorBIC, creditorId }
  // payments: [{ debtorName, iban, bic?, amount, mandateId, mandateDate(YYYY-MM-DD), remittance }]

  const now = new Date();
  const san = (s) => sanitizeTextForSEPA(String(s || ""));

  // Prepare SEPA document (pain.008.001.02 as in your createXMLSepaData example)
  const doc = new SEPA.Document("pain.008.001.02");

  // Group header
  const grpHdrID = `${san(creditor?.creditorBIC) || "MSG"}2600${now.getTime()}`;
  doc.grpHdr.id = grpHdrID;
  doc.grpHdr.created = now;
  doc.grpHdr.initiatorName = san(creditor?.creditorName);

  // Payment info
  const info = doc.createPaymentInfo();
  // createXMLSepaData adds one day to collection date
  info.collectionDate = dayjs(collectionDate).add(1, "day").toDate();
  info.creditorIBAN = cleanIban(creditor?.creditorIBAN || "");
  info.creditorBIC = san(creditor?.creditorBIC || "");
  info.creditorName = san(creditor?.creditorName || "");
  info.creditorId = san(creditor?.creditorId || "");
  info.sequenceType = "RCUR";
  info.batchBooking = true;
  doc.addPaymentInfo(info);

  let sepaValidationError = false;

  payments.forEach((p) => {
    const tx = info.createTransaction();

    tx.debtorName = san(p.debtorName);

    // IBAN cleanup & validation
    const iban = cleanIban(p.iban || "");
    if (!validateIBAN(iban)) {
      // mark validation error and skip adding this tx
      sepaValidationError = { p };
      return;
    }
    tx.debtorIBAN = iban;

    // Optional BIC
    if (p.bic) {
      const cleanedBic = String(p.bic).replace(/\s+/g, "").toUpperCase();
      if (isValidBIC(cleanedBic)) {
        tx.debtorBIC = cleanedBic;
      } else {
        // invalid BIC: omit debtorBIC so sepa lib will produce Othr/Id NOTPROVIDED behavior
        // (optionally you could collect/report these invalid entries)
      }
    }

    // Mandate
    tx.mandateId = san(p.mandateId || "");
    tx.mandateSignatureDate = p.mandateDate
      ? dayjs(p.mandateDate).toDate()
      : undefined;

    // Amount
    const amt = Number(p.amount || 0);
    if (!amt || Number.isNaN(amt)) {
      sepaValidationError = { p };

      return;
    }
    tx.amount = parseFloat(amt);

    // Remittance / purpose
    tx.remittanceInfo = san(p.remittance || "");

    // Keep same behaviour as createXMLSepaData example
    tx.end2endId = "NOTPROVIDED";

    info.addTransaction(tx);
  });

  if (sepaValidationError) {
    alert(
      `Ungültige IBAN erkannt! IBAN: ${sepaValidationError?.p?.iban} Inhaber: ${sepaValidationError?.p?.debtorName}`
    );
    return null;
  }

  let finalData = doc.toString();
  // Remove InstrId (some banks reject it)
  finalData = finalData.replace(/<InstrId.*?<\/InstrId>/g, "");
  // Ensure UTF-8 (some browsers produce encoding="null")
  finalData = finalData.replace('encoding="null"', 'encoding="UTF-8"');

  return finalData;
}

// ---------- Helpers for building SEPA rows ----------
export function buildSepaRows({
  members,
  contracts,
  monthLabel,
  remittanceTemplate,
  additionalFeePerMember = 0,
  remittanceNote = "",
  selectedSiteIds = [],
}) {
  const memberById = new Map(members.map((m) => [String(m._id || m.id), m]));
  const rows = [];
  const extraFee = Math.max(0, Number(additionalFeePerMember) || 0);
  const extraRemittance = String(remittanceNote || "").trim();
  const selectedSiteIdSet = new Set(
    selectedSiteIds.map((siteId) => String(siteId || "")).filter(Boolean)
  );

  const target = parseMmYyyy(monthLabel);
  if (!target) return rows;

  contracts.forEach((c) => {
    const iban = cleanIban(c?.iban || "");
    if (!iban || !validateIBAN(iban)) return;

    const linked = (c?.hasMembers || [])
      .map((id) => memberById.get(String(id)))
      .filter(Boolean)
      .filter((member) => {
        if (selectedSiteIdSet.size === 0) return true;
        const memberSiteId = String(
          member?.standortRef?._id || member?.standortRef || ""
        );
        return selectedSiteIdSet.has(memberSiteId);
      });
    const eligibleMembers = [];

    // Only members eligible for this month OR with a registration fee to collect
    // (We still check isCollectableForMonth() to avoid collecting for ineligible statuses,
    // but a newly created active member with payRegistrationFee should pass it.)
    const dueParts = [];
    const regParts = []; // NEW: track registration fee-only entries

    linked.forEach((m) => {
      const eligible = isCollectableForMonth(m, monthLabel);
      if (eligible) {
        eligibleMembers.push(m);
      }

      // registration fee processing
      const regFeeRaw = Number(m?.registrationFee) || 0;
      const wantsRegFee = !!m?.payRegistrationFee && regFeeRaw > 0;

      // monthly dues (only if eligible)
      if (eligible) {
        const monthsDue = monthsDiffInclusive(m?.payedTill, monthLabel);
        const contribution = Number(m?.contribution) || 0;
        const amountDue = Math.max(0, monthsDue) * contribution;

        if (amountDue > 0) {
          const pt = parseMmYyyy(m?.payedTill);
          const start = pt ? addMonths(pt, 1) : target;
          dueParts.push({
            m,
            monthsDue,
            amountDue,
            start,
          });
        }
      }

      // registration fee can be collected along with next run:
      // include it even if no monthly dues (or not eligible for dues),
      // but only for ACTIVE or TERMINATED-with-duty members (eligible = true).
      if (eligible && wantsRegFee) {
        regParts.push({
          m,
          regAmount: regFeeRaw,
        });
      }
    });

    // If nothing to collect (no dues, no reg fees, no extra fee), skip this contract
    if (
      dueParts.length === 0 &&
      regParts.length === 0 &&
      !(extraFee > 0 && eligibleMembers.length > 0)
    )
      return;

    // Sum amounts
    const amountDues = dueParts.reduce((s, x) => s + x.amountDue, 0);
    const amountReg = regParts.reduce((s, x) => s + x.regAmount, 0);
    const amountExtra = extraFee * eligibleMembers.length;
    const amount = amountDues + amountReg + amountExtra;

    // Determine mandate meta (oldest contractDate among involved members, fallback today)
    const involvedForMandate = [
      ...dueParts.map((p) => p.m),
      // ...regParts.map((p) => p.m),
    ];
    const mandateMembers =
      involvedForMandate.length > 0 ? involvedForMandate : eligibleMembers;
    if (mandateMembers.length === 0) return;

    const mandateDate =
      mandateMembers
        .map((m) =>
          m?.contractDate ? dayjs(m.contractDate).format("YYYY-MM-DD") : null
        )
        .filter(Boolean)
        .sort()[0] || dayjs().format("YYYY-MM-DD");

    const mandateId =
      mandateMembers
        .map((m) => m?.memberNumber)
        .filter(Boolean)
        .join(", ") || `VN-${c._id || c.id}`;

    const debtorName =
      c?.owner ||
      `${mandateMembers[0]?.lastName ?? ""} ${
        mandateMembers[0]?.firstName ?? ""
      }`.trim();

    // Names text for remittance
    const names = mandateMembers
      .map((m) => `${m?.lastName ?? ""} ${m?.firstName ?? ""}`.trim())
      .join(", ");

    // Period note only comes from dues (reg fee has no month range)
    const membersStarts = dueParts.map((x) => x.start);
    const periodNote =
      dueParts.length > 0
        ? buildPeriodNote(membersStarts, monthLabel)
        : monthLabel;

    // Overdue explanation (only if any member has >= 2 months due)
    const maxMemberOverdue = Math.max(...dueParts.map((x) => x.monthsDue), 0);
    const hasMemberWithOverdue2 = maxMemberOverdue >= 2;

    // Build base remittance
    const currencyFormatter = new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    });
    const registrationNote =
      amountReg > 0 ? `Anmeldung ${currencyFormatter.format(amountReg)}` : "";
    const overdueNote = hasMemberWithOverdue2 ? "inkl Rueckstaende" : "";

    const remittanceFromTemplate = remittanceTemplate
      ? renderRemittanceTemplate(remittanceTemplate, {
          bookMonth: monthLabel,
          nextPayedTill: monthLabel,
          firstMember: mandateMembers[0] || null,
          memberNames: names,
          contractOwner: debtorName,
          periodNote,
          amountFormatted: currencyFormatter.format(amount),
          registrationNote,
          overdueNote,
        }).trim()
      : "";

    const remittanceBase = dueParts.length
      ? `Mitgliedsbeitrag Taekwondo ${periodNote} ${names}`
      : `Mitgliedsbeitrag Taekwondo ${names}`;

    const overdueSuffix = hasMemberWithOverdue2 ? ` inkl Rueckstaende` : "";

    const regSuffix = registrationNote ? ` ${registrationNote}` : "";
    const extraSuffix = extraRemittance ? ` ${extraRemittance}` : "";

    const baseRemittance = remittanceFromTemplate
      ? remittanceFromTemplate
      : `${remittanceBase}${overdueSuffix}${regSuffix}`;

    const remittance = `${baseRemittance}${extraSuffix}`.slice(0, 140);

    // NEW: "terminated indicator" should consider all involved members
    const terminatedCount = mandateMembers.filter(
      (m) => (m?.status || "").toLowerCase() === "terminated"
    ).length;
    const hasTerminated = terminatedCount > 0;

    // What to show as "Mitglieder" count in preview: dues + reg-fee participants (unique members)
    const uniqueIds = new Set(mandateMembers.map((m) => String(m._id || m.id)));

    rows.push({
      contractId: String(c._id || c.id),
      debtorName,
      iban,
      bic: c?.bic || "",
      amount,
      mandateId,
      mandateDate,
      remittance,
      membersCount: uniqueIds.size,
      // IMPORTANT: only members that had dues contribute to payedTill update
      memberIds: dueParts.map(({ m }) => String(m._id || m.id)),
      // Optional (kept if you want to use elsewhere)
      regMemberIds: regParts.map(({ m }) => String(m._id || m.id)), // <-- not used for payedTill
      maxMemberOverdue,
      periodNote,
      hasTerminated,
      terminatedCount,
    });
  });

  rows.sort((a, b) => a.debtorName.localeCompare(b.debtorName, "de"));
  return rows;
}
